/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext, useMemo } from 'react';
import {
    arrayOf, func, object, shape, string,
    bool,
} from 'prop-types';
import {  Grid, makeStyles, useMediaQuery } from '@material-ui/core';
import Slider from 'react-slick';
import { useUIDSeed } from 'react-uid';
import { useDispatch, useSelector } from 'react-redux';
import { CirclePlay } from 'lucide-react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ResponsiveImage from '../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useIsDisplayChannel from '../../../../../helpers/hooks/useIsDisplayChannel';

const useStyles = makeStyles((theme) => ({
    altImagesBox: {
        girdArea: 'productImageAlt',
        display: 'inline-block',
        flexWrap: 'wrap',
        marginRight: '10px',
        maxWidth: '80px',
        [theme?.breakpoints?.down('md')]: {
            order: '2',
            margin: '0 0 0 0',
            display: 'flex',
            maxWidth: '80px',
        },
        '@media (max-width: 1024px)': {
            maxWidth: '80px',
        },
        '@media (max-width: 768px)': {
            maxWidth: '60px',
        },
        '@media (max-width:600px)': {
            maxWidth: '80px',
        },
        '@media (max-width:375px)': {
            maxWidth: '73px',
        },
    },

    mainSliderBox: ({ altImagesCount }) => ({
        display: 'grid',
        gridArea: 'productImageAlt',
        gridTemplateColumns: `repeat(${altImagesCount}, 80px)`,
        columnGap: '10px',
        overflow: 'auto',
        paddingBottom: 5,
        '&>div': {
            overflow: 'hidden',
        },
        '@media (max-width: 1024px)': {
            gridTemplateColumns: `repeat(${altImagesCount}, 80px)`,
            columnGap: '10px',
        },
        '@media (max-width: 768px)': {
            gridTemplateColumns: `repeat(${altImagesCount}, 60px)`,
            columnGap: '8px',
        },
        '@media (max-width:600px)': {
            gridTemplateColumns: `repeat(${altImagesCount}, 80px)`,
            columnGap: '8px',
            marginTop: '7px',
        },
        '@media (max-width:375px)': {
            gridTemplateColumns: `repeat(${altImagesCount}, 73px)`,
            columnGap: '8px',
            marginTop: '7px',
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(156, 156, 156, 1)',  // scroll bar color
        },
        '&::-webkit-scrollbar': {  /* Works on scrollbar for Chrome, Safari and Opera */
            width: '5px',
            backgroundColor: 'transparent',
            height: '5px',
        },
        '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            backgroundColor: 'transparent',
        },
        scrollbarColor: 'rgba(156, 156, 156, 1) transparent',
    }),
    circlePlay: {
        position: 'absolute',
        '& svg': {
            mixBlendMode: 'overlay',
            width: '4em',
            height: '4em',
        },
    },
}));

const CarouselAltImg = ({
    classes, buildImgPath, setProductImage, selectedSku, videoBlock,
    data, previewImage, mainImage, altImages, path,
}) => {
    const seed = useUIDSeed();
    const { featureFlags = {}  } = useContext(CrossMerch);
    const isMediumDevice = useMediaQuery('(max-width: 1025px)');
    const ffAltV3Enabled = featureFlags['is-pdp-alt-v3-enabled'];
    const isDisplayChannel = useIsDisplayChannel();
    const isPlaPage = useIsPlaPage();
    const foodPlaRedesign = useSelector(getFeatureFlag('which-food-pla-design'));
    const plaThreeColumn = (isPlaPage || isDisplayChannel) && foodPlaRedesign === 'three_column';
    const slideShowItems = plaThreeColumn && !isMediumDevice ? 2.5 : 3;

    const altImageList = [];
    const dispatch = useDispatch();
    const trackEventObj = {
        eventCategory: 'product image',
        eventAction: 'click',
    };

    const onClickSlider = (value) => {
        dispatch(trackEvent({
            eventCategory: 'product carousel',
            eventAction: value,
            eventLabel: `${data.baseCode}`,
        }));
    };

    const track = (eventLabel) => {
        dispatch(trackEvent({
            ...trackEventObj,
            eventLabel,
        }));
    };
    const isGif = (name) => {
        if (name.includes('_gif')) {
            return <div className={classes.circlePlay}><CirclePlay /></div>;
        }
        return <></>;
    };
    altImageList.push(
        <button
            type="button"
            className={classes.altImageButton}
            onClick={() => {
                setProductImage(selectedSku?.image?.name);
                track(`${data?.baseCode} | ${buildImgPath(selectedSku?.image?.path, selectedSku?.image?.name)} | 1`);
            }}
        >
            <ResponsiveImage path={buildImgPath(selectedSku?.image?.path, selectedSku?.image?.name, true)} alt={mainImage?.imgName} className={classes.altImage} />
        </button>,
    );

    if (videoBlock?.enabled) {
        altImageList.push(
            <button
                type="button"
                className={`${classes.altImageButton} ${classes.videoAltButton}`}
                onClick={() => {
                    setProductImage(videoBlock?.video?.url, videoBlock?.video?.content_type);
                    track(`${data?.baseCode} | ${previewImage} | 2`);
                }}
            >
                <ResponsiveImage path={previewImage} alt="Video" className={classes.altImage} params={{ desktop: 'quality=100', tablet: 'quality=100', mobile: 'quality=100' }} />
            </button>,
        );
    }

    if (altImages.length > 0) {
        altImages.forEach((imgName, index) => {
            altImageList.push(
                <button
                    key={`btnImg${imgName}`}
                    type="button"
                    className={classes.altImageButton}
                    onClick={() => {
                        setProductImage(imgName);
                        track(`${data?.baseCode} | ${buildImgPath(path, imgName, false)} | ${videoBlock?.enabled ? index + 3 : index + 2}`);
                    }}
                >
                    {isGif(imgName)}
                    <img
                        src={buildImgPath(path, imgName, true)}
                        alt={mainImage?.imgName}
                        className={classes.altImage}
                    />
                </button>,
            );
        });
    }
    const styles = useStyles({ altImagesCount: altImageList?.length || 'auto' });
    const memoAltImages = useMemo(() => (
        <>
            {  altImageList.length > 1 && (
                <Grid data-testid="v3-alt-images" container className={styles.mainSliderBox}>
                    {altImageList?.map((item) => (
                        <div key={seed(item)} className={`${styles.altImagesBox}`}>
                            {item}
                        </div>
                    ))}
                </Grid>
            )}
        </>
    ), [styles]);

    const nextArrow = () => (
        <div>
            <ChevronRightIcon onClick={() => onClickSlider('next')} />
        </div>
    );

    const backArrow = () => (
        <div>
            <ChevronLeftIcon onClick={() => onClickSlider('previous')} />
        </div>
    );

    const isAltWithoutSlider = (ffAltV3Enabled && isMediumDevice) || false;

    if (isAltWithoutSlider) {
        return <>{memoAltImages}</>;
    }

    return (
        altImageList.length > 1 && (
            <Grid container className={classes.mainSliderContainer}>
                <Slider
                    dots={false}
                    speed={500}
                    infinite={false}
                    slidesToShow={altImageList.length > 2 ? 4 : altImageList.length}
                    slidesToScroll={1}
                    nextArrow={nextArrow()}
                    prevArrow={backArrow()}
                    variableWidth={!(altImageList.length > 1)}
                    className={`carousel-container ${classes.carouselContainer}`}
                    responsive={[
                        {
                            breakpoint: plaThreeColumn ? 1280 : 1024,
                            settings: {
                                slidesToShow: slideShowItems,
                                slidesToScroll: 1,
                            },
                        }]}
                >
                    {altImageList?.map((item) => (
                        <div key={seed(item)} className={`${classes.altImageContainer}`}>
                            {item}
                        </div>
                    ))}
                </Slider>
            </Grid>
        )
    );
};

CarouselAltImg.propTypes = {
    classes: object.isRequired,
    buildImgPath: func.isRequired,
    setProductImage: func.isRequired,
    selectedSku: shape({
        image: shape({
            path: string.isRequired,
            image: string.isRequired,
        }),
    }),
    videoBlock: shape({
        enabled: false,
        video: shape({
            title: string.isRequired,
            url: string.isRequired,
            content_type: string.isRequired,
        }),
    }),
    altImages: arrayOf(
        shape({
            string,
        }),
    ),
    previewImage: string.isRequired,
    mainImage: shape({
        imgName: string.isRequired,
    }),
    path: string.isRequired,
    data: shape({
        baseCode: string,
    }),
};
CarouselAltImg.defaultProps = {
    selectedSku: {},
    videoBlock: {},
    mainImage: {},
    altImages: [],
    data: {},
};
export default (CarouselAltImg);
